/**
 * The case overview: one page for what the case is, how it has grown over time and where to go
 * next. It joins the old overview (status, actions, assistant, description, investigations,
 * entity resolution) with the dashboard (tiles, sources per month, hypotheses, Monitor lists,
 * *Spis k dátumu*). Every case reads as a file over time; a file that has received nothing yet
 * shows its roadmap and a way to start instead of an empty chart.
 *
 * Presentational: the dashboard state comes in as data, the parts that read their own data
 * (status picker, PDF menu, assistant, description, investigations, entity resolution) as slots.
 */
import { Import, Upload } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AsOfBanner } from '@/components/case-as-of/AsOfBanner'
import { AsOfPicker } from '@/components/case-as-of/AsOfPicker'
import { AssessmentPanel } from '@/components/case-dashboard/AssessmentPanel'
import { DashboardTile } from '@/components/case-dashboard/DashboardTile'
import type {
  CaseDashboardModel,
  CaseDashboardState,
  DashboardAsOf,
} from '@/components/case-dashboard/dashboard-view'
import { MonitorPanels } from '@/components/case-dashboard/MonitorPanels'
import { SourcesPerMonthChart } from '@/components/case-dashboard/SourcesPerMonthChart'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DashboardFacts, DashboardFailed, DashboardLoading } from './CaseOverviewParts'

export interface CaseOverviewSlots {
  /** The case's status control. */
  status: ReactNode
  /** The header's actions: refresh counts, PDF, upload, edit. */
  actions: ReactNode
  /** The way into the assistant: ask about the file. */
  assistant: ReactNode
  /** The case roadmap; shown only while the file has received nothing. */
  journey?: ReactNode
  /** Under the file: the description, the saved investigations and entity resolution. */
  about: { description: ReactNode; investigations: ReactNode; entityResolution: ReactNode }
}

export interface CaseOverviewViewProps {
  /** The case's dashboard read, its retry and the *Spis k dátumu* it is read as of. */
  dashboard: { state: CaseDashboardState; onRetry: () => void; asOf: DashboardAsOf }
  slots: CaseOverviewSlots
  /** Opens the upload of documents; the start card of an empty file offers it. */
  onUpload: () => void
}

/** A file that has not received a single source yet. */
function hasNoSources(dashboard: CaseDashboardModel): boolean {
  return (dashboard.tiles.find(tile => tile.key === 'sources')?.total ?? 0) === 0
}

function Header({
  dashboard,
  asOf,
  slots,
}: {
  dashboard: CaseDashboardModel
  asOf: DashboardAsOf
  slots: CaseOverviewSlots
}) {
  return (
    <header className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">{slots.status}</div>
        <div className="flex flex-wrap items-center gap-2">{slots.actions}</div>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <h1 className="min-w-0 break-words text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          {dashboard.name}
        </h1>
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <DashboardFacts dashboard={dashboard} />
          <AsOfPicker day={asOf.day} onChange={asOf.onChange} />
        </div>
      </div>
    </header>
  )
}

/** In place of the chart while the file is empty: what to do first. */
function StartCard({ onUpload }: { onUpload: () => void }) {
  const { t } = useTranslation()
  return (
    <Card
      variant="dashed"
      className="flex flex-col items-center justify-center gap-3 rounded-xl p-8 text-center lg:col-span-2"
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
        <Import aria-hidden className="size-6 text-primary" />
      </div>
      <h3 className="text-base font-semibold text-foreground">{t('caseOverview.start.title')}</h3>
      <p className="max-w-md text-sm text-muted-foreground">{t('caseOverview.start.body')}</p>
      <Button onClick={onUpload}>
        <Upload className="size-4" />
        {t('caseOverview.start.upload')}
      </Button>
    </Card>
  )
}

function FileOverTime({
  dashboard,
  asOf,
  slots,
  onUpload,
}: {
  dashboard: CaseDashboardModel
  asOf: DashboardAsOf
  slots: CaseOverviewSlots
  onUpload: () => void
}) {
  const empty = hasNoSources(dashboard)
  return (
    <>
      <Header dashboard={dashboard} asOf={asOf} slots={slots} />
      {empty ? slots.journey : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {dashboard.tiles.map(tile => (
          <DashboardTile key={tile.key} tile={tile} />
        ))}
      </div>
      {slots.assistant}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {empty ? (
          <StartCard onUpload={onUpload} />
        ) : (
          <Card className="min-w-0 rounded-xl p-5 lg:col-span-2">
            <SourcesPerMonthChart months={dashboard.months} sourceKinds={dashboard.sourceKinds} />
          </Card>
        )}
        <AssessmentPanel panels={dashboard.panels} />
      </div>
      {empty ? null : <MonitorPanels panels={dashboard.panels} />}
    </>
  )
}

function About({ about }: { about: CaseOverviewSlots['about'] }) {
  const { t } = useTranslation()
  return (
    <section aria-labelledby="case-overview-about" className="flex flex-col gap-3">
      <h2 id="case-overview-about" className="text-sm font-semibold text-foreground">
        {t('caseOverview.about.title')}
      </h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-xl p-4">{about.description}</Card>
        {about.investigations}
        {about.entityResolution}
      </div>
    </section>
  )
}

export function CaseOverviewView({ dashboard, slots, onUpload }: CaseOverviewViewProps) {
  const { state, onRetry, asOf } = dashboard
  return (
    <div className="flex flex-col gap-6">
      {asOf.day ? <AsOfBanner day={asOf.day} onClear={() => asOf.onChange(null)} /> : null}
      {state.status === 'ready' ? (
        <FileOverTime dashboard={state.dashboard} asOf={asOf} slots={slots} onUpload={onUpload} />
      ) : (
        <>
          {state.status === 'loading' ? (
            <DashboardLoading />
          ) : (
            <DashboardFailed onRetry={onRetry} />
          )}
          {slots.assistant}
        </>
      )}
      <About about={slots.about} />
    </div>
  )
}
